import type { ProviderProps, StepType } from '@reactour/tour'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  agentHubBookingOnboardingSelector,
  getVisibleAgentHubBookingOnboardingSteps,
} from './agentHubBookingOnboardingSteps'
import { useAgentHubBookingOnboardingCopy } from './useAgentHubBookingOnboardingCopy'

const ACCOUNT_STEP_POPOVER_SHIFT_X = 6

function shiftPopoverLeft(left: string | number | undefined, offset: number): string | number | undefined {
  if (typeof left === 'number') {
    return left + offset
  }

  if (typeof left === 'string' && left.endsWith('px')) {
    return `${parseFloat(left) + offset}px`
  }

  return left
}

function useAgentHubBookingOnboardingTourSteps() {
  const intl = useIntl()
  const copy = useAgentHubBookingOnboardingCopy()
  const includeTabletOnlySteps = useScreenSizeUp('tablet')

  const skipLabel = intl.formatMessage({
    id: 'ZswB3-Ry8aDLPcD4qBwj',
    defaultMessage: 'Skip',
  })
  const backLabel = intl.formatMessage({
    id: '82LzhGADc-xp2MnEOBrK',
    defaultMessage: 'Back',
  })
  const nextLabel = intl.formatMessage({
    id: 'LRsLp4S3jpgowh0cdO7a',
    defaultMessage: 'Next',
  })
  const doneLabel = intl.formatMessage({
    id: 'kP4DPV8x6S2y_YDhsEnM',
    defaultMessage: 'Done',
  })
  const closeLabel = intl.formatMessage({
    id: 'BAzgGlxcGND6eep39jZO',
    defaultMessage: 'Close walkthrough',
  })

  const steps = useMemo<Array<StepType>>(() => (
    getVisibleAgentHubBookingOnboardingSteps(includeTabletOnlySteps).map((step) => {
      const selector = agentHubBookingOnboardingSelector(step.id)
      const stepCopy = copy[step.id]

      return {
        selector,
        resizeObservables: [selector],
        mutationObservables: [selector],
        padding: { mask: [4, 8] },
        ...(step.id === 'account' ? {
          styles: {
            popover: (base) => ({
              ...base,
              left: shiftPopoverLeft(base.left, ACCOUNT_STEP_POPOVER_SHIFT_X),
            }),
          },
        } : {}),
        content: (
          <Group direction="vertical" gap={12}>
            <Heading id="agent-hub-booking-onboarding-title" variant="heading6">
              {stepCopy.title}
            </Heading>
            <BodyText id="agent-hub-booking-onboarding-body" variant="medium" colour="neutral-two">
              {stepCopy.body}
            </BodyText>
          </Group>
        ),
      }
    })
  ), [copy, includeTabletOnlySteps])

  const badgeContent = useCallback<NonNullable<ProviderProps['badgeContent']>>(({ currentStep, totalSteps }) => (
    intl.formatMessage({
      id: 'y7r4CM7unAduA-K-W0cs',
      defaultMessage: 'Step {current} of {total}',
    }, {
      current: currentStep + 1,
      total: totalSteps,
    })
  ), [intl])

  return {
    backLabel,
    badgeContent,
    closeLabel,
    doneLabel,
    nextLabel,
    skipLabel,
    steps,
  }
}

export default useAgentHubBookingOnboardingTourSteps
